import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChatBubbleBottomCenterTextIcon, MegaphoneIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/buttons/Button';
import { CardNoticeMessage } from '../components/CardNoticeMessage';
import { NoticeFormDrawer } from './NoticeFormDrawer';
import { canManageCourseNotices } from './noticePermissions';
import { getNoticeMessages } from '../../../redux/actions/noticeMessage';
import { connectSocket } from '../../../services/socket';
import './notices-panel.css';

export const NoticesPanel = ({ courseId }) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.user);
    const { courseSelected } = useSelector((state) => state.course);
    const { participants } = useSelector((state) => state.chatRealtime);
    const { allNoticeMessages } = useSelector((state) => state.noticeMessage);

    const [formOpen, setFormOpen] = useState(false);
    const [editingNotice, setEditingNotice] = useState(null);

    const canManage = canManageCourseNotices(user, courseSelected, participants);

    useEffect(() => {
        if (courseId) {
            dispatch(getNoticeMessages(courseId));
        }
    }, [courseId, dispatch]);

    useEffect(() => {
        if (!courseId) return undefined;

        let mounted = true;
        let detach = null;

        const init = async () => {
            try {
                const socket = await connectSocket();
                if (!mounted || !socket) return;

                const onNoticeUpdate = (payload) => {
                    if (!mounted) return;
                    if (String(payload?.courseId) !== String(courseId)) return;
                    dispatch(getNoticeMessages(courseId));
                };

                socket.on('notice_update', onNoticeUpdate);
                detach = () => {
                    try {
                        socket?.off?.('notice_update', onNoticeUpdate);
                    } catch {
                        // socket ya cerrado
                    }
                };
            } catch {
                // sin socket: el listado se refresca al crear/editar localmente
            }
        };

        init();

        return () => {
            mounted = false;
            if (detach) detach();
        };
    }, [courseId, dispatch]);

    const openCreate = () => {
        setEditingNotice(null);
        setFormOpen(true);
    };

    const openEdit = (notice) => {
        setEditingNotice(notice);
        setFormOpen(true);
    };

    const closeForm = (options = {}) => {
        setFormOpen(false);
        if (options.reset) {
            setEditingNotice(null);
        }
    };

    return (
        <div className="notices-panel">
            <div className="notices-panel__top">
                <div className="notices-panel__heading">
                    <h2 className="notices-panel__title">Muro de avisos</h2>
                    <p className="notices-panel__count" aria-live="polite">
                        {(allNoticeMessages?.length || 0) === 1
                            ? '1 aviso'
                            : `${allNoticeMessages?.length || 0} avisos`}
                    </p>
                </div>
                {canManage ? (
                    <Button
                        variant="gradient"
                        fullWidth
                        className="notices-panel__create"
                        icon={<ChatBubbleBottomCenterTextIcon className="w-4 h-4" aria-hidden="true" />}
                        onClick={openCreate}
                    >
                        Nuevo aviso
                    </Button>
                ) : null}
            </div>

            <div className="notices-panel__list">
                {allNoticeMessages?.length ? (
                    <div className="notices-panel__list-inner course-tab-stagger">
                        {allNoticeMessages.map((notice) => (
                            <CardNoticeMessage
                                key={notice._id}
                                notice={notice}
                                canManage={canManage}
                                onEdit={canManage ? () => openEdit(notice) : undefined}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="notices-panel__empty">
                        <MegaphoneIcon className="notices-panel__empty-icon" aria-hidden="true" />
                        <p className="notices-panel__empty-text">No hay publicaciones de avisos.</p>
                    </div>
                )}
            </div>

            {canManage ? (
                <NoticeFormDrawer
                    isOpen={formOpen}
                    onClose={closeForm}
                    courseId={courseId}
                    userId={user?._id}
                    notice={editingNotice}
                />
            ) : null}
        </div>
    );
};
